<script setup lang="ts">
/**
 * Single full-width cell for the mobile services table: one card per session.
 * Left: service type, a group line (name, optional description, age — the
 * description is ellipsized, the age never shrinks) and the colour-coded
 * performers. Right: time, men/women balance and revenue (muted when zero).
 * Mirrors `docs/sketches/mobile/services-table.html`.
 */
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { formatMoney, currencySymbol } from '@/utils/formatMoney'
import { groupAgeShort } from '@/utils/groupAge'
import type { GenderBucket } from '@/utils/gender'

const props = withDefaults(
  defineProps<{
    time?: string
    /** Service type label (description, or the `Service type #id` fallback). */
    serviceType?: string
    /** Group display name; omit for an ad-hoc session (the group line is hidden). */
    groupName?: string
    /** Optional group description, shown after the name and ellipsized when long. */
    groupDescription?: string
    /** Whole years since the group started. */
    groupYears?: number
    /** Months (0-11) after `groupYears`. */
    groupMonths?: number
    menCount?: number
    womenCount?: number
    /** Session revenue in major currency units. */
    revenue?: number
    performers?: { id: string; name: string; gender: GenderBucket }[]
  }>(),
  { revenue: 0, performers: () => [] },
)

const { t } = useI18n()

const groupAge = computed(() => groupAgeShort(props.groupYears ?? 0, props.groupMonths ?? 0, t))
const revenueFormatted = computed(
  () => formatMoney({ amount: props.revenue, precision: 0, fractionDigits: 2 }).formatted,
)
</script>

<template>
  <td class="service-mobile-card font-tufte">
    <div class="smc">
      <div class="smc__left">
        <div class="smc__type">{{ serviceType }}</div>
        <div v-if="groupName" class="smc__group-line">
          <span class="smc__group">{{ groupName }}</span>
          <span v-if="groupDescription" class="smc__desc"
            ><span class="smc__dot">·</span> {{ groupDescription }}</span
          >
          <span class="smc__age"><span class="smc__dot">·</span> {{ groupAge }}</span>
        </div>
        <div v-if="performers.length > 0" class="smc__perf">
          <span
            v-for="p in performers"
            :key="p.id"
            :class="p.gender === 'women' ? 'smc__perf--women' : 'smc__perf--men'"
            >{{ p.name }}</span
          >
        </div>
      </div>
      <div class="smc__right">
        <span class="smc__time">{{ time }}</span>
        <span class="smc__balance"
          ><span class="smc__men">{{ menCount }}m</span><span class="smc__sep">/</span
          ><span class="smc__women">{{ womenCount }}w</span></span
        >
        <span class="smc__revenue" :class="{ 'smc__revenue--zero': revenue <= 0 }"
          >{{ revenueFormatted }}<span class="smc__cur">{{ currencySymbol }}</span></span
        >
      </div>
    </div>
  </td>
</template>

<style scoped>
/* Overrides ServicesTab's shared `.services-table--mobile :deep(td)` padding
 * (same `!important` technique as the date-header row cell). */
.service-mobile-card {
  padding: 0.9rem 1.25rem !important;
}

.smc {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.85rem;
}

.smc__left {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.smc__type {
  font-size: var(--text-tufte-lg);
  line-height: 1.2;
}

.smc__group-line {
  display: flex;
  align-items: baseline;
  gap: 0.3rem;
  min-width: 0;
  font-family: var(--font-tufte-sans);
  font-size: var(--text-tufte-xs);
}

.smc__group {
  flex: 0 0 auto;
  max-width: 60%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--color-tufte-text);
}

.smc__desc {
  flex: 0 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--color-tufte-muted);
}

.smc__age {
  flex: 0 0 auto;
  white-space: nowrap;
  color: var(--color-tufte-muted);
}

.smc__dot {
  color: var(--color-tufte-muted);
}

.smc__perf {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  font-family: var(--font-tufte-sans);
  font-size: var(--text-tufte-sm);
}

.smc__perf--men {
  color: var(--color-gender-men);
}

.smc__perf--women {
  color: var(--color-gender-women);
}

.smc__right {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.25rem;
  font-family: var(--font-tufte-mono);
  font-variant-numeric: tabular-nums;
}

.smc__time {
  font-size: 1.15rem;
  font-weight: 600;
  color: var(--color-tufte-text);
  line-height: 1.1;
}

.smc__balance {
  font-size: 1.05rem;
}

.smc__men {
  color: var(--color-gender-men);
}

.smc__women {
  color: var(--color-gender-women);
}

.smc__sep {
  color: var(--color-tufte-muted);
}

.smc__revenue {
  font-size: 1.05rem;
  color: var(--color-money-positive);
  white-space: nowrap;
}

.smc__revenue--zero {
  color: var(--color-tufte-muted);
}

.smc__cur {
  font-size: var(--text-tufte-xs);
  color: var(--color-tufte-muted);
  margin-left: 0.12rem;
}
</style>
